<template>
  <b-overlay :show="disabled" spinner-variant="light" spinner-type="grow" spinner-small>
    <b-row>
      <b-col>
        <b-form-select v-model="widget" :options="filteredWidgetList" :disabled="disabled" />
      </b-col>

      <b-col cols="auto">
        <b-button variant="primary" @click="addWidget" :disabled="disabled">
          {{ buttonText }}
        </b-button>
      </b-col>
    </b-row>
  </b-overlay>
</template>

<script>
import { reactive } from 'vue'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'PagesWidgetPicker',
  props: {
    disabled: { type: Boolean },
    buttonText: { type: String, default: 'Add Widget' },
    omit: { type: Array, required: false, default: () => [] },
    allow: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      widget: 'text',
      widgetList: [
        { value: 'toplist', text: 'Toplist' },
        { value: 'tabs', text: 'Tabs' },
        { value: 'newsletter', text: 'Newsletter' },
        { value: 'related_products', text: 'Related Products' },
        { value: 'contact_form', text: 'Contact Form' },
        { value: 'image', text: 'Image' },
        { value: 'text', text: 'Text' },
        { value: 'category_toplist', text: 'Category Toplist' },
        { value: 'category_list', text: 'Category List' },
        { value: 'one_two_three', text: 'Static One Two Three Widget' },
        { value: 'featured_categories', text: 'Featured Categories' },
        { value: 'place_list', text: 'Place List' },
        { value: 'featured_places', text: 'Featured Places' },
        { value: 'featured_places_in_norway', text: 'Featured Places in Norway' },
        { value: 'static_two_col', text: 'Static 2 col (text and image)' },
        { value: 'static_giftcard', text: 'Static Open Giftcard Feature' },
        { value: 'dynamic_toplist', text: 'Dynamic Toplist' },
        { value: 'review_slideshow', text: 'A slideshow for reviews' },
        { value: 'dynamic_seo_block', text: 'Dynamic SEO block' },
        { value: 'custom_toplist', text: 'Custom Toplist' },
        { value: 'latest_products', text: 'Latest products' },
        { value: 'video', text: 'Video' },
        { value: 'sustainability', text: 'Sustainability Widget' },
        { value: 'links_list', text: 'Links List' },
        { value: 'recommended', text: 'Recommended' },
        { value: 'inspiration_toplist', text: 'Inspiration Toplist' },
        { value: 'gift_wrappers', text: 'Gift Wrappers' },
        { value: 'media', text: 'Media' },
        { value: 'tiles', text: 'Tiles' },
        { value: 'hero', text: 'Hero' },
        { value: 'giftcard_buybox', text: 'Giftcard Buybox' },
      ],
    }
  },
  computed: {
    list() {
      return new Map(
        Object.entries({
          toplist: { type: 'toplist', content: { id: 0 } },
          text: { type: 'text', content: { content: '' } },
          newsletter: { type: 'newsletter', content: { title: '', full_width: true } },
          related_products: { type: 'related_products', content: { title: 'Related Products', count: 4 } },
          contact_form: { type: 'contact_form', content: { title: 'Say Hi!' } },
          tabs: { type: 'tabs', content: { tabs: [], title: '', link_text: '', link_url: '', type: 'tab_content' } },
          review_slideshow: {
            type: 'review_slideshow',
            content: { limit: 12, rating: 3, type: 'review_slideshow_items' },
          },
          image: { type: 'image', content: { src: '', caption: '' } },
          category_toplist: {
            type: 'category_toplist',
            content: { id: 0, title: '', link_text: '', link_url: '', header: true, is_news: false },
          },
          one_two_three: { type: 'one_two_three', content: { title: '' } },
          static_giftcard: { type: 'static_giftcard', content: 'static' },
          dynamic_toplist: { type: 'dynamic_toplist', content: 'static' },
          static_two_col: {
            type: 'static_two_col',
            content: {
              text: '',
              btn_text: '',
              btn_link: '',
              image_position: 'left',
              image: { src: '', type: 'image' },
            },
          },
          featured_categories: {
            type: 'featured_categories',
            content: { items: [], title: '', link_text: '', link_url: '', type: 'featured_categories_content' },
          },
          category_list: {
            type: 'category_list',
            content: {
              items: [],
              title: '',
              link_text: '',
              link_url: '',
              type: 'category_list_content',
              full_width: true,
            },
          },
          featured_places: {
            type: 'featured_places',
            content: {
              items: [],
              title: '',
              link_text: '',
              link_url: '',
              widget_display: 'single',
              type: 'featured_places_content',
            },
          },
          featured_places_in_norway: {
            type: 'featured_places_in_norway',
            content: {
              items: [],
              title: '',
              link_text: '',
              link_url: '',
              widget_display: 'single',
              type: 'featured_places_in_norway_content',
            },
          },
          place_list: {
            type: 'place_list',
            content: {
              items: [],
              title: '',
              link_text: '',
              link_url: '',
              type: 'place_list_content',
              full_width: true,
            },
          },
          dynamic_seo_block: { type: 'dynamic_seo_block', content: 'static' },
          custom_toplist: {
            type: 'custom_toplist',
            content: {
              product_ids: '',
              title: '',
              link_text: '',
              link_url: '',
            }
          },
          latest_products: { type: 'latest_products', content: 'static' },
          video: {
            type: 'video',
            content: {
              bullet_points: [],
              title: '',
              text: '',
              video: {
                desktop: null,
                tablet: null,
                mobile: null,
                tablet_same_as_desktop: true,
                mobile_same_as_desktop: true,
              }
            }
          },
          media: {
            type: 'media',
            content: {
              bullet_points: [],
              title: '',
              text: '',
              media: {
                desktop: null,
                tablet: null,
                mobile: null,
                tablet_same_as_desktop: true,
                mobile_same_as_desktop: true,
              }
            }
          },
          sustainability: { type: 'sustainability', content: { title: '' } },
          links_list: { type: 'links_list', content: { title: '', links: [], full_width: true, } },
          recommended: {
            type: 'recommended',
            content: { title: 'Anbefalte artikler', links: [], full_width: true },
          },
          inspiration_toplist: {
            type: 'inspiration_toplist',
            content: {
              link_title: '',
              link: '',
              products: [],
              starting_index: 1,
              full_width: true,
            },
          },
          gift_wrappers: { type: 'gift_wrappers', content: 'static' },
          tiles: {
            type: 'tiles',
            content: {
              tiles: [],
              title: '',
              text_top: '',
              text_bottom: '',
              mode: 'static',
              height: null,
              heightMobile: null,
              slides: null,
              slidesMobile: null,
            },
          },
          hero: {
            type: 'hero',
            content: {
              icons: [],
              title: '',
              text: '',
              bg_image: '',
            },
          },
          giftcard_buybox: { type: 'giftcard_buybox', content: { title: '' } },
        })
      )
    },
    filteredWidgetList() {
      return this.allow.length > 0
        ? this.widgetList.filter(widget => this.allow.includes(widget.value))
        : this.widgetList.filter(widget => !this.omit.includes(widget.value))
    },
  },
  methods: {
    addWidget() {
      const widget = reactive(_.cloneDeep(this.list.get(this.widget)))
      widget.id = uuidv4()
      this.$emit('widget', widget)
    },
  },
}
</script>
